import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Plus, Trash2, Eye, EyeOff, Loader2, Upload, ArrowUp, ArrowDown,
  Image as ImageIcon, Video, Type, LayoutGrid, Newspaper, Tag, DollarSign,
  MessageSquare, Sparkles, PencilLine, ExternalLink, Package, Wand2, BadgePercent,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast, Toaster } from 'sonner';
import api from '@/services/api';
import EntityPicker from './EntityPicker';

const TYPE_META = {
  hero:              { label: 'Hero',              icon: Sparkles,     desc: 'Full-bleed hero video/image with headline + CTAs' },
  image_banner:      { label: 'Image Banner',      icon: ImageIcon,    desc: 'Static image banner with copy + CTA' },
  video_banner:      { label: 'Video Banner',      icon: Video,        desc: 'Autoplay video banner with copy + CTA' },
  editorial_marquee: { label: 'Editorial Marquee', icon: PencilLine,   desc: 'Scrolling words strip' },
  price_edit:        { label: 'Price Edit',        icon: DollarSign,   desc: 'Under-price groupings' },
  offer_edit:        { label: 'Live Offers',       icon: BadgePercent, desc: 'Auto-filled rail of products with active offers' },
  category_grid:     { label: 'Category Grid',     icon: LayoutGrid,   desc: 'Curated categories tile' },
  brand_index:       { label: 'Brand Index',       icon: Tag,          desc: 'Featured brand rail' },
  product_showcase:  { label: 'Product Showcase',  icon: Package,      desc: 'Hand-picked product carousel' },
  manifesto:         { label: 'Manifesto',         icon: Newspaper,    desc: 'Chaptered brand story' },
  text_block:        { label: 'Rich Text',         icon: Type,         desc: 'Sanitized HTML block' },
};

const TypePill = ({ type }) => {
  const meta = TYPE_META[type] || { label: type, icon: MessageSquare };
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-medium" data-testid={`type-pill-${type}`}>
      <Icon className="h-3 w-3" /> {meta.label}
    </span>
  );
};

const StatusPill = ({ status }) => {
  const tone = status === 'published'
    ? 'bg-emerald-100 text-emerald-800'
    : 'bg-amber-100 text-amber-900';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tone}`} data-testid={`status-pill-${status}`}>{status}</span>;
};

// ---- Media picker button ----
const MediaButton = ({ kind, value, onChange, testId }) => {
  const [busy, setBusy] = useState(false);
  const inputId = `media-${testId}`;
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', kind);
      const res = await api.post('/hq/cms/media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(res.data.url);
      toast.success('Uploaded');
    } catch (err) {
      toast.error('Upload failed', { description: err.response?.data?.detail || err.message });
    } finally { setBusy(false); e.target.value = ''; }
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={`https://cdn…/${kind}`} data-testid={`${testId}-url`} />
        <input type="file" id={inputId} className="hidden" accept={kind === 'image' ? 'image/*' : 'video/mp4,video/webm'} onChange={handleFile} data-testid={`${testId}-file`} />
        <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById(inputId).click()} disabled={busy} data-testid={`${testId}-upload-btn`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        </Button>
      </div>
      {value && kind === 'image' && <img src={value} alt="" className="h-20 rounded-md border object-cover" />}
      {value && kind === 'video' && <video src={value} className="h-20 rounded-md border" muted controls />}
    </div>
  );
};

// ==================== Per-type editors ====================

const HeroEditor = ({ content, onChange }) => {
  const c = content || {};
  const setLine = (i, v) => {
    const lines = [...(c.headline_lines || ['', '', ''])];
    lines[i] = v; onChange({ ...c, headline_lines: lines });
  };
  const setCta = (which, key, v) => onChange({ ...c, [which]: { ...(c[which] || {}), [key]: v } });
  const badges = c.trust_badges || [];
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Headline (exactly 3 lines)</Label>
        {[0, 1, 2].map(i => (
          <Input key={i} className="mt-1" value={c.headline_lines?.[i] || ''} onChange={(e) => setLine(i, e.target.value)} placeholder={`Line ${i + 1}`} data-testid={`hero-line-${i}`} />
        ))}
      </div>
      <div>
        <Label className="text-xs">Subtext</Label>
        <Textarea rows={2} value={c.subtext || ''} onChange={(e) => onChange({ ...c, subtext: e.target.value })} data-testid="hero-subtext" />
      </div>
      <div>
        <Label className="text-xs">Hero Video (webm or mp4, ≤20 MB) — optional</Label>
        <MediaButton kind="video" value={c.video_url} onChange={(v) => onChange({ ...c, video_url: v })} testId="hero-video" />
      </div>
      <div>
        <Label className="text-xs">Poster / fallback image</Label>
        <MediaButton kind="image" value={c.poster_url} onChange={(v) => onChange({ ...c, poster_url: v })} testId="hero-poster" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 border rounded-md">
          <Label className="text-xs font-semibold">Primary CTA</Label>
          <Input className="mt-1" placeholder="Label" value={c.cta_primary?.label || ''} onChange={(e) => setCta('cta_primary', 'label', e.target.value)} data-testid="hero-cta-primary-label" />
          <Input className="mt-1" placeholder="/href" value={c.cta_primary?.href || ''} onChange={(e) => setCta('cta_primary', 'href', e.target.value)} data-testid="hero-cta-primary-href" />
        </div>
        <div className="p-3 border rounded-md">
          <Label className="text-xs font-semibold">Secondary CTA</Label>
          <Input className="mt-1" placeholder="Label" value={c.cta_secondary?.label || ''} onChange={(e) => setCta('cta_secondary', 'label', e.target.value)} data-testid="hero-cta-secondary-label" />
          <Input className="mt-1" placeholder="/href" value={c.cta_secondary?.href || ''} onChange={(e) => setCta('cta_secondary', 'href', e.target.value)} data-testid="hero-cta-secondary-href" />
        </div>
      </div>
      <div>
        <Label className="text-xs">Trust badges (comma-separated, optional)</Label>
        <Textarea rows={2} value={badges.join(', ')} onChange={(e) => onChange({ ...c, trust_badges: e.target.value.split(',').map(x => x.trim()).filter(Boolean) })} data-testid="hero-trust-badges" placeholder="4.8 · 2L+ happy shoppers, 100% Authentic, Free shipping ₹999+" />
      </div>
    </div>
  );
};

const BannerEditor = ({ content, onChange, variant }) => {
  const c = content || {};
  return (
    <div className="space-y-3">
      <Input placeholder="Eyebrow (optional)" value={c.eyebrow || ''} onChange={(e) => onChange({ ...c, eyebrow: e.target.value })} data-testid="banner-eyebrow" />
      <Input placeholder="Heading" value={c.heading || ''} onChange={(e) => onChange({ ...c, heading: e.target.value })} data-testid="banner-heading" />
      <Textarea placeholder="Subtext (optional)" rows={2} value={c.subtext || ''} onChange={(e) => onChange({ ...c, subtext: e.target.value })} data-testid="banner-subtext" />
      <div>
        <Label className="text-xs">Image</Label>
        <MediaButton kind="image" value={c.image_url} onChange={(v) => onChange({ ...c, image_url: v })} testId="banner-image" />
      </div>
      {variant === 'video_banner' && (
        <div>
          <Label className="text-xs">Video (autoplay)</Label>
          <MediaButton kind="video" value={c.video_url} onChange={(v) => onChange({ ...c, video_url: v })} testId="banner-video" />
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Input placeholder="CTA Label" value={c.cta_label || ''} onChange={(e) => onChange({ ...c, cta_label: e.target.value })} data-testid="banner-cta-label" />
        <Input placeholder="/cta-href" value={c.cta_href || ''} onChange={(e) => onChange({ ...c, cta_href: e.target.value })} data-testid="banner-cta-href" />
      </div>
    </div>
  );
};

const MarqueeEditor = ({ content, onChange }) => {
  const c = content || {};
  const words = c.words || [];
  return (
    <div className="space-y-3">
      <Label className="text-xs">Words (comma-separated)</Label>
      <Textarea rows={3} value={words.join(', ')} onChange={(e) => onChange({ ...c, words: e.target.value.split(',').map(x => x.trim()).filter(Boolean) })} data-testid="marquee-words" />
      <div>
        <Label className="text-xs">Scroll speed (optional, px/s)</Label>
        <Input type="number" value={c.speed ?? ''} onChange={(e) => onChange({ ...c, speed: e.target.value ? parseFloat(e.target.value) : undefined })} data-testid="marquee-speed" />
      </div>
    </div>
  );
};

const PriceEditEditor = ({ content, onChange }) => {
  const c = content || {};
  const groups = c.groups || [];
  const setGroup = (i, k, v) => {
    const next = groups.map((g, idx) => idx === i ? { ...g, [k]: v } : g);
    onChange({ ...c, groups: next });
  };
  return (
    <div className="space-y-3">
      <Input placeholder="Eyebrow" value={c.eyebrow || ''} onChange={(e) => onChange({ ...c, eyebrow: e.target.value })} data-testid="pe-eyebrow" />
      <Input placeholder="Heading" value={c.heading || ''} onChange={(e) => onChange({ ...c, heading: e.target.value })} data-testid="pe-heading" />
      <div className="space-y-2">
        {groups.map((g, i) => (
          <div key={i} className="grid grid-cols-[1fr_120px_auto] gap-2">
            <Input placeholder="Under ₹499" value={g.label || ''} onChange={(e) => setGroup(i, 'label', e.target.value)} data-testid={`pe-group-${i}-label`} />
            <Input type="number" placeholder="499" value={g.max_price ?? ''} onChange={(e) => setGroup(i, 'max_price', e.target.value ? parseFloat(e.target.value) : 0)} data-testid={`pe-group-${i}-max`} />
            <Button type="button" size="sm" variant="ghost" onClick={() => onChange({ ...c, groups: groups.filter((_, idx) => idx !== i) })}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <Button type="button" size="sm" variant="outline" onClick={() => onChange({ ...c, groups: [...groups, { label: '', max_price: 0 }] })} data-testid="pe-add-group">+ Add group</Button>
      </div>
    </div>
  );
};

const OfferEditEditor = ({ content, onChange }) => {
  const c = content || {};
  return (
    <div className="space-y-3">
      <Input placeholder="Eyebrow (e.g. Limited time)" value={c.eyebrow || ''} onChange={(e) => onChange({ ...c, eyebrow: e.target.value })} data-testid="oe-eyebrow" />
      <Input placeholder="Heading (e.g. Live Offers)" value={c.heading || ''} onChange={(e) => onChange({ ...c, heading: e.target.value })} data-testid="oe-heading" />
      <p className="text-xs text-slate-500">Products are pulled automatically from the active offers in Offer Settings — publish to show the rail on the homepage, keep as draft to hide it, and use the position arrows to place it (it can sit above the hero).</p>
    </div>
  );
};

const IdListEditor = ({ content, onChange, field, entityKind, testPrefix, allowEmpty = false }) => {
  const c = content || {};
  const ids = c[field] || [];
  return (
    <div className="space-y-3">
      <Input placeholder="Eyebrow" value={c.eyebrow || ''} onChange={(e) => onChange({ ...c, eyebrow: e.target.value })} data-testid={`${testPrefix}-eyebrow`} />
      <Input placeholder="Heading" value={c.heading || ''} onChange={(e) => onChange({ ...c, heading: e.target.value })} data-testid={`${testPrefix}-heading`} />
      <Textarea rows={2} placeholder="Subtext (optional)" value={c.subtext || ''} onChange={(e) => onChange({ ...c, subtext: e.target.value })} data-testid={`${testPrefix}-subtext`} />
      <div>
        <Label className="text-xs mb-1 block">
          {entityKind === 'category' && 'Categories'}
          {entityKind === 'brand' && 'Brands'}
          {entityKind === 'product' && 'Products'}
        </Label>
        <EntityPicker
          kind={entityKind}
          value={ids}
          onChange={(next) => onChange({ ...c, [field]: next })}
          testPrefix={testPrefix}
          allowEmpty={allowEmpty}
        />
      </div>
    </div>
  );
};

const ManifestoEditor = ({ content, onChange }) => {
  const c = content || {};
  const chapters = c.chapters || [];
  const setCh = (i, k, v) => onChange({ ...c, chapters: chapters.map((ch, idx) => idx === i ? { ...ch, [k]: v } : ch) });
  return (
    <div className="space-y-3">
      {chapters.map((ch, i) => (
        <div key={i} className="p-3 border rounded-md space-y-2">
          <div className="grid grid-cols-[80px_1fr_auto] gap-2 items-center">
            <Input placeholder="01" value={ch.no || ''} onChange={(e) => setCh(i, 'no', e.target.value)} data-testid={`mf-${i}-no`} />
            <Input placeholder="Chapter title" value={ch.title || ''} onChange={(e) => setCh(i, 'title', e.target.value)} data-testid={`mf-${i}-title`} />
            <Button type="button" size="sm" variant="ghost" onClick={() => onChange({ ...c, chapters: chapters.filter((_, idx) => idx !== i) })}><Trash2 className="h-4 w-4" /></Button>
          </div>
          <Textarea rows={3} placeholder="Body copy…" value={ch.text || ''} onChange={(e) => setCh(i, 'text', e.target.value)} data-testid={`mf-${i}-text`} />
          <MediaButton kind="image" value={ch.img} onChange={(v) => setCh(i, 'img', v)} testId={`mf-${i}-img`} />
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange({ ...c, chapters: [...chapters, { no: '', title: '', text: '', img: '' }] })} data-testid="mf-add-chapter">+ Add chapter</Button>
    </div>
  );
};

const TextBlockEditor = ({ content, onChange }) => (
  <div className="space-y-2">
    <Label className="text-xs">Rich HTML (dangerous tags stripped server-side)</Label>
    <Textarea rows={8} className="font-mono text-xs" value={content?.html || ''} onChange={(e) => onChange({ ...content, html: e.target.value })} data-testid="text-html" />
  </div>
);

const EDITORS = {
  hero: HeroEditor,
  image_banner: (p) => <BannerEditor {...p} variant="image_banner" />,
  video_banner: (p) => <BannerEditor {...p} variant="video_banner" />,
  editorial_marquee: MarqueeEditor,
  price_edit: PriceEditEditor,
  offer_edit: OfferEditEditor,
  category_grid:    (p) => <IdListEditor {...p} field="category_ids" entityKind="category" testPrefix="cg" allowEmpty />,
  brand_index:      (p) => <IdListEditor {...p} field="brand_ids"    entityKind="brand"    testPrefix="bi" allowEmpty />,
  product_showcase: (p) => <IdListEditor {...p} field="product_ids"  entityKind="product"  testPrefix="ps" />,
  manifesto: ManifestoEditor,
  text_block: TextBlockEditor,
};

// ==================== Main page ====================

export default function CMSHomePage() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // { id?, type, content, status, position }
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [seedOpen, setSeedOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/hq/cms/sections', { params: { placement: 'home' } });
      setSections(res.data.items || []);
    } catch (e) {
      toast.error('Failed to load sections', { description: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing({ type: 'hero', content: {}, status: 'draft' });
    setEditorOpen(true);
  };

  const openEdit = (s) => {
    setEditing({ id: s.id, type: s.type, content: s.content, status: s.status });
    setEditorOpen(true);
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      if (editing.id) {
        await api.patch(`/hq/cms/sections/${editing.id}`, {
          type: editing.type, content: editing.content, status: editing.status,
        });
        toast.success('Section updated');
      } else {
        await api.post('/hq/cms/sections', {
          placement: 'home', type: editing.type, content: editing.content, status: editing.status,
        });
        toast.success('Section created');
      }
      setEditorOpen(false); setEditing(null);
      await load();
    } catch (e) {
      toast.error('Save failed', { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const togglePublish = async (s) => {
    try {
      const url = s.status === 'published' ? 'unpublish' : 'publish';
      await api.post(`/hq/cms/sections/${s.id}/${url}`);
      await load();
    } catch (e) {
      toast.error('Failed', { description: e.response?.data?.detail || e.message });
    }
  };

  const remove = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/hq/cms/sections/${deleting.id}`);
      toast.success('Section deleted');
      setDeleting(null);
      await load();
    } catch (e) {
      toast.error('Delete failed', { description: e.response?.data?.detail || e.message });
    }
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= sections.length) return;
    const next = [...sections];
    [next[index], next[target]] = [next[target], next[index]];
    setSections(next);
    try {
      await api.post('/hq/cms/sections/reorder', { ordered_ids: next.map(s => s.id) });
    } catch (e) {
      toast.error('Reorder failed'); await load();
    }
  };

  const seedDefaults = async (replace) => {
    setSeeding(true);
    try {
      const res = await api.post('/hq/cms/sections/seed-defaults', null, { params: { replace } });
      toast.success(`Seeded ${res.data.count} sections`, {
        description: replace ? 'Existing sections replaced.' : 'Default home page ready.',
      });
      setSeedOpen(false);
      await load();
    } catch (e) {
      toast.error('Seed failed', { description: e.response?.data?.detail || e.message });
    } finally { setSeeding(false); }
  };

  const Editor = editing ? EDITORS[editing.type] : null;

  const renderPreview = (s) => {
    const c = s.content || {};
    if (s.type === 'hero') return c.headline_lines?.join(' · ');
    if (s.type === 'image_banner' || s.type === 'video_banner') return c.heading;
    if (s.type === 'editorial_marquee') return (c.words || []).slice(0, 6).join(' • ');
    if (s.type === 'price_edit') return `${(c.groups || []).length} price groups`;
    if (s.type === 'offer_edit') return c.heading || 'Live Offers (auto)';
    if (s.type === 'category_grid') return `${(c.category_ids || []).length || 'All'} categories`;
    if (s.type === 'brand_index') return `${(c.brand_ids || []).length || 'All'} brands`;
    if (s.type === 'product_showcase') return `${(c.product_ids || []).length} products`;
    if (s.type === 'manifesto') return `${(c.chapters || []).length} chapters`;
    if (s.type === 'text_block') return (c.html || '').replace(/<[^>]+>/g, '').slice(0, 120);
    return '—';
  };

  return (
    <div className="p-6 space-y-6" data-testid="cms-home-page">
      <Toaster richColors position="top-right" />

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Home Page CMS</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage every section on the storefront home page — no code deploys needed.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setSeedOpen(true)} data-testid="seed-defaults-btn">
            <Wand2 className="h-4 w-4 mr-2" /> Seed Soonyoung Defaults
          </Button>
          <Button variant="outline" asChild data-testid="preview-storefront-btn">
            <a href="/api/ecom/cms/sections" target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4 mr-2" /> Preview JSON</a>
          </Button>
          <Button onClick={openNew} data-testid="new-section-btn"><Plus className="h-4 w-4 mr-2" /> New Section</Button>
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Loading…</div>
      ) : sections.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <Sparkles className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
          <div className="font-medium">No sections yet</div>
          <div className="text-sm text-muted-foreground mt-1">Add your first section to start building the home page.</div>
          <Button className="mt-4" onClick={openNew} data-testid="empty-new-btn"><Plus className="h-4 w-4 mr-2" /> New Section</Button>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {sections.map((s, i) => {
            const meta = TYPE_META[s.type] || {};
            const Icon = meta.icon || MessageSquare;
            return (
              <Card key={s.id} data-testid={`section-row-${s.id}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="flex flex-col gap-1">
                    <Button size="icon" variant="ghost" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`move-up-${s.id}`}><ArrowUp className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => move(i, 1)} disabled={i === sections.length - 1} data-testid={`move-down-${s.id}`}><ArrowDown className="h-4 w-4" /></Button>
                  </div>
                  <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <TypePill type={s.type} />
                      <StatusPill status={s.status} />
                      <span className="text-xs text-muted-foreground">position {s.position}</span>
                    </div>
                    <div className="text-sm truncate">{renderPreview(s)}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => togglePublish(s)} data-testid={`toggle-publish-${s.id}`}>
                      {s.status === 'published' ? <><EyeOff className="h-4 w-4 mr-1" /> Unpublish</> : <><Eye className="h-4 w-4 mr-1" /> Publish</>}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`edit-${s.id}`}><PencilLine className="h-4 w-4" /></Button>
                    <Button size="sm" variant="outline" onClick={() => setDeleting(s)} data-testid={`delete-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Editor dialog */}
      <Dialog open={editorOpen} onOpenChange={(v) => { if (!v) { setEditorOpen(false); setEditing(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="editor-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit section' : 'New section'}</DialogTitle>
            <DialogDescription>All changes stay in draft until you publish.</DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Section type</Label>
                <Select value={editing.type} onValueChange={(v) => setEditing({ ...editing, type: v, content: {} })} disabled={!!editing.id}>
                  <SelectTrigger data-testid="editor-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_META).map(([k, m]) => (
                      <SelectItem key={k} value={k}>{m.label} — <span className="text-muted-foreground text-xs">{m.desc}</span></SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {Editor && <Editor content={editing.content} onChange={(content) => setEditing({ ...editing, content })} />}

              <div>
                <Label className="text-xs">Status</Label>
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger data-testid="editor-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => { setEditorOpen(false); setEditing(null); }}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="editor-save-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete section?</AlertDialogTitle>
            <AlertDialogDescription>
              This section will disappear from the home page. It's a soft delete — reach out to engineering if you need it back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Seed defaults dialog */}
      <AlertDialog open={seedOpen} onOpenChange={setSeedOpen}>
        <AlertDialogContent data-testid="seed-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Seed Soonyoung defaults</AlertDialogTitle>
            <AlertDialogDescription>
              This inserts 6 pre-designed sections (Hero, Price Edit, Marquee, Category Grid, Manifesto, Brand Index) drawn from the Soonyoung reference storefront.
              {sections.length > 0 && (
                <span className="block mt-2 text-amber-700 font-medium">
                  ⚠️ Your home page already has {sections.length} section(s). Choose "Replace all" to soft-delete them first.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 flex-wrap sm:flex-nowrap">
            <AlertDialogCancel disabled={seeding}>Cancel</AlertDialogCancel>
            {sections.length === 0 ? (
              <AlertDialogAction onClick={() => seedDefaults(false)} disabled={seeding} data-testid="seed-append-btn">
                {seeding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Seed defaults
              </AlertDialogAction>
            ) : (
              <AlertDialogAction onClick={() => seedDefaults(true)} disabled={seeding} className="bg-amber-600 hover:bg-amber-700" data-testid="seed-replace-btn">
                {seeding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Replace all
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
